import React from 'react'
import { Link } from 'react-router-dom'
import {Clock, MapPin, ExternalLink, Star, CreditCard, Train, Bus, Zap} from 'lucide-react'

function NaritaTransferPage() {
  const topPicks = [
    {
      id: 1,
      type: 'JR Pass',
      name: '7-Day Japan Rail Pass',
      price: '¥29,650',
      rating: 4.9,
      link: 'https://www.japanrailpass.net/'
    },
    {
      id: 2,
      type: 'Transfer',
      name: 'N\'EX Round Trip Ticket',
      price: '¥4,070',
      rating: 4.8,
      link: '#'
    },
    {
      id: 3,
      type: 'Hotel',
      name: 'Hotel Near Tokyo Station',
      price: '¥13,000/night',
      rating: 4.6,
      link: '#'
    }
  ]

  return (
    <div className="pt-16 min-h-screen bg-white">
      {/* Hero Image - Clean logistics style */}
      <div className="relative h-[50vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1564859228273-274232fdb516?w=1200&q=80"
          alt="Narita Express train"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
        
        {/* Breadcrumb */}
        <div className="absolute top-8 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            <Link to="/" className="text-gray-600 hover:text-[#00E5FF] transition-colors">
              Home
            </Link>
            <span className="text-gray-400 mx-2">/</span>
            <Link to="/logistics" className="text-gray-600 hover:text-[#00E5FF] transition-colors">
              Logistics & Transport
            </Link>
          </div>
        </div>

        {/* Title */}
        <div className="absolute bottom-12 left-0 right-0">
          <div className="max-w-5xl mx-auto px-4">
            <span className="inline-block px-4 py-1 bg-gray-900 text-white font-bold text-sm rounded-full mb-4">
              Logistics & Transport
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Narita Airport to Tokyo: The Complete Guide
            </h1>
            <div className="flex items-center gap-4 text-gray-600 text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                10 min read
              </span>
              <span>•</span>
              <span>Updated January 2024</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Layout - White/Clean Design */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <article className="lg:w-2/3">
            {/* Quick Summary Box - Clean Style */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="w-6 h-6 text-[#00E5FF]" />
                Quick Summary
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Getting from Narita Airport to Tokyo is easier than you think. You have 4 main options: Narita Express (fastest), 
                Keisei Skyliner (cheapest fast option), Airport Limousine Bus (most convenient for hotels), or taxi (most expensive). 
                This guide breaks down every option with prices, times, and when to use each.
              </p>

              {/* Quick Comparison Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-300">
                      <th className="py-3 text-gray-900 font-bold">Option</th>
                      <th className="py-3 text-gray-900 font-bold">Time</th>
                      <th className="py-3 text-gray-900 font-bold">Price</th>
                      <th className="py-3 text-gray-900 font-bold">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-200">
                      <td className="py-3 font-semibold">N'EX (Narita Express)</td>
                      <td className="py-3">60 min</td>
                      <td className="py-3">¥3,070</td>
                      <td className="py-3">Speed & Comfort</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-3 font-semibold">Keisei Skyliner</td>
                      <td className="py-3">45 min</td>
                      <td className="py-3">¥2,520</td>
                      <td className="py-3">Budget & Speed</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="py-3 font-semibold">Limousine Bus</td>
                      <td className="py-3">90-120 min</td>
                      <td className="py-3">¥3,200</td>
                      <td className="py-3">Hotel Drop-off</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-semibold">Taxi</td>
                      <td className="py-3">60-90 min</td>
                      <td className="py-3">¥20,000+</td>
                      <td className="py-3">Groups/Luxury</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <a href="https://www.japanrailpass.net/" target="_blank" rel="noopener noreferrer" className="flex-1 px-6 py-3 bg-gray-900 text-white font-bold rounded-full hover:bg-gray-800 transition-all flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Book JR Pass
                </a>
                <a href="https://www.keisei.co.jp/keisei/tetudou/skyliner/us/index.php" target="_blank" rel="noopener noreferrer" className="flex-1 px-6 py-3 bg-[#00E5FF] text-white font-bold rounded-full hover:bg-[#00B8CC] transition-all flex items-center justify-center gap-2">
                  <Train className="w-4 h-4" />
                  Book Skyliner
                </a>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 space-y-6">
                <p className="text-xl leading-relaxed text-gray-900">
                  Narita Airport is 60km from central Tokyo. That's about an hour by express train, 
                  or 90+ minutes by bus. Here's exactly how to pick the right option based on your budget, 
                  luggage situation, and final destination.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Option 1: Narita Express (N'EX)</h2>
                
                <div className="bg-gray-100 border-l-4 border-[#00E5FF] rounded-r-xl p-6 my-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Train className="w-8 h-8 text-[#00E5FF]" />
                    <h3 className="text-xl font-bold text-gray-900 m-0">Best For: First-timers, JR Pass Holders</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 mb-0">
                    <li>• <strong>Time:</strong> 60 minutes to Tokyo Station</li>
                    <li>• <strong>Price:</strong> ¥3,070 one-way (or FREE with JR Pass)</li>
                    <li>• <strong>Frequency:</strong> Every 30 minutes</li>
                    <li>• <strong>Stops:</strong> Tokyo, Shinagawa, Shibuya, Shinjuku, Ikebukuro</li>
                  </ul>
                </div>

                <p>
                  The Narita Express is the official airport train operated by JR East. It's comfortable, reliable, 
                  has plenty of luggage space, and if you have a <strong>JR Pass, it's completely free</strong>. 
                  This alone can save you ¥6,000+ on round-trip airport transfers.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-3">How to Book</h3>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>Buy tickets at the JR East Travel Service Center in the arrivals hall</li>
                  <li>Or buy online in advance at <a href="https://www.eki-net.com/en/" className="text-[#00E5FF] hover:underline">JR East website</a></li>
                  <li>Show your JR Pass if you have one (no extra ticket needed)</li>
                  <li>Reserved seats recommended during peak hours</li>
                </ol>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Option 2: Keisei Skyliner</h2>
                
                <div className="bg-gray-100 border-l-4 border-[#FF006E] rounded-r-xl p-6 my-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Zap className="w-8 h-8 text-[#FF006E]" />
                    <h3 className="text-xl font-bold text-gray-900 m-0">Best For: Budget Travelers, Ueno Area</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 mb-0">
                    <li>• <strong>Time:</strong> 45 minutes to Ueno Station (fastest!)</li>
                    <li>• <strong>Price:</strong> ¥2,520 one-way</li>
                    <li>• <strong>Frequency:</strong> Every 20-40 minutes</li>
                    <li>• <strong>Stops:</strong> Nippori, Ueno (then transfer to Tokyo Metro)</li>
                  </ul>
                </div>

                <p>
                  The Skyliner is actually faster than N'EX and cheaper. The catch? It only goes to Ueno/Nippori, 
                  so you'll need to transfer to the Tokyo Metro to reach other areas. If you're staying in Ueno, Asakusa, 
                  or eastern Tokyo, this is your best bet.
                </p>

                <div className="bg-[#00E5FF]/10 border border-[#00E5FF] rounded-xl p-6 my-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">💡 Money-Saving Combo</h4>
                  <p className="text-gray-700 mb-0">
                    Buy the "Skyliner + Tokyo Metro 72-hour Pass" combo for ¥3,800. You get the airport transfer 
                    PLUS unlimited Tokyo Metro rides for 3 days. Saves you ¥1,500+ compared to buying separately.
                  </p>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Option 3: Airport Limousine Bus</h2>
                
                <div className="bg-gray-100 border-l-4 border-gray-400 rounded-r-xl p-6 my-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Bus className="w-8 h-8 text-gray-600" />
                    <h3 className="text-xl font-bold text-gray-900 m-0">Best For: Hotels, Heavy Luggage</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 mb-0">
                    <li>• <strong>Time:</strong> 90-120 minutes (traffic dependent)</li>
                    <li>• <strong>Price:</strong> ¥3,200 one-way</li>
                    <li>• <strong>Routes:</strong> Direct to major hotels in Shinjuku, Shibuya, Tokyo Station</li>
                  </ul>
                </div>

                <p>
                  The limousine bus drops you directly at major hotels, which is super convenient if you have lots of luggage. 
                  The downside? Traffic can make the journey unpredictable. Budget at least 2 hours during rush hour.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Option 4: Taxi</h2>
                <p>
                  Fixed-rate taxis from Narita cost around ¥20,000-25,000 to central Tokyo. Only worth it if you're 
                  traveling with 3-4 people and splitting the cost, or if you have excessive luggage. Most taxis accept 
                  credit cards now.
                </p>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Which Should You Choose?</h2>
                
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Decision Matrix:</h3>
                  <ul className="space-y-3 text-gray-700 list-none pl-0">
                    <li>✅ <strong>Have a JR Pass?</strong> → Take N'EX (it's free!)</li>
                    <li>✅ <strong>Budget traveler?</strong> → Take Skyliner</li>
                    <li>✅ <strong>Staying near Ueno/Asakusa?</strong> → Take Skyliner</li>
                    <li>✅ <strong>Heavy luggage + hotel in Shinjuku/Shibuya?</strong> → Take Limousine Bus</li>
                    <li>✅ <strong>Group of 4+ people?</strong> → Split a taxi</li>
                  </ul>
                </div>

                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">Cost Comparison</h2>
                <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                  <ul className="space-y-3 text-gray-700 list-none pl-0">
                    <li className="flex justify-between pb-3 border-b border-gray-200">
                      <span>N'EX Round Trip</span>
                      <strong className="text-[#00E5FF]">¥6,140</strong>
                    </li>
                    <li className="flex justify-between pb-3 border-b border-gray-200">
                      <span>Skyliner Round Trip</span>
                      <strong className="text-[#00E5FF]">¥5,040</strong>
                    </li>
                    <li className="flex justify-between pb-3 border-b border-gray-200">
                      <span>Skyliner + 3-Day Metro Pass</span>
                      <strong className="text-[#00E5FF]">¥3,800</strong>
                    </li>
                    <li className="flex justify-between pb-3">
                      <span className="text-red-600">Taxi One-Way</span>
                      <strong className="text-red-600">¥20,000+</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 bg-gray-900 p-8 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Book Your Transfer?</h3>
              <p className="text-gray-300 mb-6">
                Get your JR Pass or Skyliner tickets before you land to skip the lines at the airport.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://www.japanrailpass.net/" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-[#00E5FF] text-white font-bold rounded-full hover:bg-[#00B8CC] transition-all text-center">
                  Get JR Pass →
                </a>
                <a href="https://www.keisei.co.jp/keisei/tetudou/skyliner/us/index.php" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-white text-gray-900 font-bold rounded-full hover:bg-gray-100 transition-all text-center">
                  Book Skyliner →
                </a>
              </div>
            </div>
          </article>

          {/* Sidebar - Top 3 Picks */}
          <aside className="lg:w-1/3">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-[#FF006E]" fill="#FF006E" />
                  Recommended Passes
                </h3>
                
                <div className="space-y-4">
                  {topPicks.map((pick) => (
                    <a key={pick.id} href={pick.link} target="_blank" rel="noopener noreferrer" className="block bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-[#00E5FF] transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-bold mb-1">{pick.type}</div>
                          <h4 className="text-gray-900 font-semibold text-sm">{pick.name}</h4>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-500 text-sm">
                          <Star className="w-3 h-3" fill="currentColor" />
                          {pick.rating}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#00E5FF] font-bold text-sm">{pick.price}</span>
                        <span className="text-gray-900 hover:text-[#00E5FF] transition-colors text-xs font-semibold">
                          View Deal →
                        </span>
                      </div>
                    </a>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-gray-500 text-xs">
                    💰 Affiliate Disclosure: We earn from qualifying bookings.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

export default NaritaTransferPage